import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowUp, Mic, X } from 'lucide-react'
import { Screen } from '../components/Screen'
import { AiActionCard } from './AiActionScreen'
import { RichText } from '../components/RichText'
import { Skeleton } from '../components/States'
import { replyPending, useHubChat, withContext, type HubItem } from '../data/hubChat'
import type { QueuedQuestion } from '../data/hubQueue/store'
import type { HubAction, HubMessage } from '../data/types'
import { VOICE_PROBLEM_TEXT, useVoiceInput } from '../data/useVoiceInput'
import type { HubContext } from '../platform/platform'

/** Подпись к отказу по правилу тарифа: это не «ошибка», повтор не поможет. */
export function limitText(reason: string | undefined): string {
  if (reason === 'soft_locked') {
    return 'Вопрос не отправлен: подписка клуба закончилась, и Штаб сейчас не может работать с данными.'
  }
  return 'Вопрос не отправлен: запросы к Штабу на этот месяц закончились. Счётчик обнулится в начале следующего месяца, больше запросов - на другом тарифе.'
}

function formatSeconds(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** «Голосовое, 0:12» */
export function voiceLabel(q: Pick<QueuedQuestion, 'durationMs'>): string {
  return `Голосовое, ${formatSeconds(Math.max(1, Math.round((q.durationMs ?? 0) / 1000)))}`
}

/**
 * S13 «Штаб» в PWA (SC09). Текст работает всегда, голос - только по нажатию.
 * Контекст экрана, с которого открыт Штаб, виден над полем и явно
 * добавляется к вопросу; клуб и направление чат не меняет.
 */
export function HubChatScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const context = (location.state as { context?: HubContext } | null)?.context
  const [useCtx, setUseCtx] = useState(!!context?.label)
  const contextLabel = useCtx ? context?.label : undefined

  const chat = useHubChat()
  const [draft, setDraft] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  const back = useCallback(() => {
    if (location.key !== 'default') navigate(-1)
    else navigate('/today', { replace: true })
  }, [location.key, navigate])

  // Запись сохраняется на устройстве сразу, распознаётся при отправке (без
  // сети - когда сеть появится). Распознанный текст уходит сам, как голосовое
  // в боте, и виден в ленте целиком - с контекстом, если он добавлен.
  const recorder = useVoiceInput((audio, durationMs) => void chat.sendVoice(audio, durationMs, contextLabel))

  // Новое сообщение или пришедший ответ - лента прокручивается к концу
  const last = chat.items[chat.items.length - 1]
  const lastKey = last ? itemKey(last) + (last.kind === 'message' ? last.message.status : last.phase) : ''
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' })
    // Подсказка «нет сети» под полем сдвигает ленту - конец остаётся видимым
  }, [chat.items.length, lastKey, chat.online])

  const submit = (e?: FormEvent) => {
    e?.preventDefault()
    if (!draft.trim()) return
    const text = withContext(draft, contextLabel)
    const typed = draft
    setDraft('')
    // Не сохранилось даже на устройстве - текст возвращается в поле
    void chat.send(text).then((ok) => {
      if (!ok) setDraft((d) => d || typed)
    })
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  const openAthletes = (action: HubAction) => {
    navigate('/athletes', { state: { hubFilter: { ids: action.athleteIds, label: action.label } } })
  }

  // Без сети запрос истории стоит на паузе: это не «загрузка», а ожидание сети
  const historyOffline = chat.history.isPending && (chat.history.fetchStatus === 'paused' || !chat.online)

  let log
  if (chat.history.isPending && !historyOffline && chat.items.length === 0) {
    log = <Skeleton rows={2} label="Загружаю разговор" />
  } else {
    log = (
      <>
        {historyOffline && (
          <div className="pc-alert" role="status">
            <span style={{ flex: 1 }}>Прежний разговор загрузится, когда появится сеть.</span>
          </div>
        )}
        {chat.history.isError && (
          <div className="pc-alert" role="alert">
            <span style={{ flex: 1 }}>Прежний разговор не загрузился. Спросить можно и сейчас.</span>
            <button type="button" className="pc-btn" onClick={() => void chat.history.refetch()}>
              Повторить
            </button>
          </div>
        )}
        {chat.items.length === 0 && !chat.history.isError && !historyOffline && (
          <div className="pc-hub-intro">
            <p>
              Спросите про расписание, посещаемость, оплаты или спортсменов - текстом или голосом. Ответ придёт
              сюда же.
            </p>
          </div>
        )}
        <ol className="pc-hub-log" aria-label="Разговор со Штабом">
          {chat.items.map((item) => (
            <li key={itemKey(item)}>
              {item.kind === 'queued' ? (
                <Queued
                  item={item}
                  persistent={chat.persistent}
                  onRetry={() => chat.retry(item.question.id)}
                  onDiscard={() => void chat.discard(item.question.id)}
                  onSubscription={() => navigate('/club/subscription')}
                />
              ) : (
                <Message
                  message={item.message}
                  waitFailed={!!chat.waitFailed[item.message.id]}
                  onRecheck={() => chat.recheck(item.message.id)}
                  onAction={openAthletes}
                />
              )}
            </li>
          ))}
        </ol>
      </>
    )
  }

  const recording = recorder.phase === 'recording'
  const busyVoice = recorder.phase !== 'idle'

  return (
    <Screen title="Штаб" back={back} hideHub hideNav layout="chat">
      <div className="pc-hub-body" aria-live="polite" aria-relevant="additions text">
        {log}
        <div ref={endRef} className="pc-hub-end" />
      </div>

      <div className="pc-composer">
        {contextLabel && (
          <div className="pc-hub-context">
            <span>
              К вопросу добавится: <strong>{contextLabel}</strong>
            </span>
            <button
              type="button"
              className="pc-icon-btn"
              aria-label="Не добавлять контекст к вопросу"
              onClick={() => setUseCtx(false)}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        )}

        <form className="pc-hub-form" onSubmit={submit}>
          <label className="pc-visually-hidden" htmlFor="pc-hub-input">
            Сообщение Штабу
          </label>
          <textarea
            id="pc-hub-input"
            className="pc-input"
            rows={1}
            placeholder="Сообщение..."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={recording}
          />
          <button
            type="submit"
            className="pc-btn pc-btn--primary pc-hub-send"
            aria-label="Отправить"
            disabled={!draft.trim() || recording}
          >
            <ArrowUp size={22} aria-hidden="true" />
          </button>
        </form>

        <div className="pc-hub-voice">
          {recorder.phase === 'idle' && (
            <button
              type="button"
              className="pc-btn pc-btn--link pc-hub-voice__start"
              onClick={() => void recorder.start()}
            >
              <Mic size={18} aria-hidden="true" />
              Записать голос
            </button>
          )}
          {recorder.phase === 'starting' && (
            <>
              <span className="pc-muted">Жду разрешения на микрофон</span>
              <button type="button" className="pc-btn" onClick={recorder.cancel}>
                Отменить
              </button>
            </>
          )}
          {recording && (
            <>
              <span className="pc-hub-rec" role="status">
                <span className="pc-hub-rec__dot" aria-hidden="true" />
                Идёт запись {formatSeconds(recorder.seconds)}
              </span>
              <button type="button" className="pc-btn" onClick={recorder.cancel}>
                Отменить
              </button>
              <button type="button" className="pc-btn pc-btn--primary" onClick={recorder.stop}>
                Готово
              </button>
            </>
          )}
        </div>

        {recorder.problem && !busyVoice && (
          <p className="pc-hub-problem" role="alert">
            {VOICE_PROBLEM_TEXT[recorder.problem]}
          </p>
        )}
        {!chat.online && (
          <p className="pc-muted pc-hub-offline" role="status">
            {chat.persistent
              ? 'Нет сети: вопросы сохранятся на устройстве и уйдут сами.'
              : 'Нет сети: вопросы уйдут сами, если не закрывать приложение - браузер не дал сохранить их на устройстве.'}
          </p>
        )}
      </div>
    </Screen>
  )
}

function itemKey(item: HubItem): string {
  return item.kind === 'queued' ? `q-${item.question.id}` : item.message.id
}

function Queued({
  item,
  persistent,
  onRetry,
  onDiscard,
  onSubscription,
}: {
  item: Extract<HubItem, { kind: 'queued' }>
  persistent: boolean
  onRetry: () => void
  onDiscard: () => void
  onSubscription: () => void
}) {
  const { question: q, phase } = item
  const voice = q.kind === 'voice'
  // Голос до распознавания показываем записью: «Голосовое, 0:12»
  const recorded = voice && !q.text
  const label = voiceLabel(q)
  const remove = (
    <button type="button" className="pc-btn pc-btn--link" onClick={onDiscard}>
      Удалить
    </button>
  )
  const retry = (
    <button type="button" className="pc-btn" onClick={onRetry}>
      Повторить
    </button>
  )

  let body
  if (phase === 'waiting_network') {
    body = (
      <div className="pc-hub-meta pc-hub-meta--wait">
        {!recorded && <span>Ждёт сети</span>}
        {!persistent && <span>Сохранено до закрытия приложения</span>}
        <span className="pc-hub-meta__actions">{remove}</span>
      </div>
    )
  } else if (phase === 'sending' || phase === 'transcribing' || phase === 'queued') {
    body = (
      <p className="pc-hub-meta" role="status">
        {phase === 'sending' ? 'Отправляется' : phase === 'transcribing' ? 'Распознаю голос' : 'В очереди'}
      </p>
    )
  } else if (phase === 'retry') {
    body = (
      <div className="pc-hub-meta pc-hub-meta--error">
        <span>
          Не отправлено: нет связи с сервером. Уйдёт само, когда связь появится. Повторить безопасно: дважды вопрос
          не уйдёт.
        </span>
        <span className="pc-hub-meta__actions">
          {retry}
          {remove}
        </span>
      </div>
    )
  } else if (phase === 'unrecognized') {
    body = (
      <div className="pc-hub-meta pc-hub-meta--error">
        <span>Не распознано: слов не разобрать или распознавание не удалось. Запись сохранена.</span>
        <span className="pc-hub-meta__actions">
          {retry}
          {remove}
        </span>
      </div>
    )
  } else if (phase === 'limit') {
    body = (
      <div className="pc-hub-meta pc-hub-meta--limit">
        <span>{limitText(q.reason)}</span>
        <span className="pc-hub-meta__actions">
          <button type="button" className="pc-btn" onClick={onSubscription}>
            Подписка клуба
          </button>
          {remove}
        </span>
      </div>
    )
  } else {
    body = (
      <div className="pc-hub-meta pc-hub-meta--error">
        <span>Не отправлено: сервер не принял вопрос.</span>
        <span className="pc-hub-meta__actions">
          {retry}
          {remove}
        </span>
      </div>
    )
  }

  return (
    <div className="pc-hub-msg pc-hub-msg--user" data-phase={phase}>
      {recorded ? (
        <p className="pc-hub-text pc-hub-voice-note">
          <Mic size={16} aria-hidden="true" />
          {phase === 'waiting_network' ? `${label}, ждёт сети` : label}
        </p>
      ) : (
        <p className="pc-hub-text">{q.text}</p>
      )}
      {body}
    </div>
  )
}

function Message({
  message,
  waitFailed,
  onRecheck,
  onAction,
}: {
  message: HubMessage
  waitFailed: boolean
  onRecheck: () => void
  onAction: (action: HubAction) => void
}) {
  if (message.role === 'user') {
    return (
      <div className="pc-hub-msg pc-hub-msg--user">
        <p className="pc-hub-text">{message.text}</p>
      </div>
    )
  }

  const pending = replyPending(message)
  return (
    <div className="pc-hub-msg pc-hub-msg--hub">
      <p className="pc-hub-who">Штаб</p>
      {pending && !waitFailed && (
        <p className="pc-hub-meta" role="status">
          Готовлю ответ
        </p>
      )}
      {pending && waitFailed && (
        <div className="pc-hub-meta pc-hub-meta--error">
          <span>Вопрос принят, но ответ пока не получен. Отправлять заново не нужно.</span>
          <span className="pc-hub-meta__actions">
            <button type="button" className="pc-btn" onClick={onRecheck}>
              Проверить ещё раз
            </button>
          </span>
        </div>
      )}
      {!pending && <RichText text={message.text || 'Ответ пустой.'} />}
      {!pending &&
        message.pendingActionIds?.map((id) => (
          <AiActionCard key={id} id={id} />
        ))}
      {!pending && message.action && (
        <button type="button" className="pc-btn pc-btn--block pc-hub-action" onClick={() => onAction(message.action!)}>
          {`${message.action.label ? `Открыть спортсменов: ${message.action.label}` : 'Открыть спортсменов'} (${message.action.athleteIds.length})`}
        </button>
      )}
    </div>
  )
}
